import { useSelector } from 'react-redux';
import React, { DependencyList, FC, PropsWithChildren, useMemo } from 'react';
import { selectPortalLoginStatus } from '@/redux/portalAuth.slice';
import {
  Listener as WebsocketListener,
  State,
  useWebsocket,
  WebsocketProvider,
} from '@/hooks/portal/ws/useWebsocket';

const { VITE_BASE_URL = '' } = import.meta.env;

const baseUrl = (() => {
  const url = VITE_BASE_URL
    ? new URL(VITE_BASE_URL)
    : new URL(window.location.href);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';

  return `${protocol}//${url.host}`;
})();

export type Event = {
  type: string;
};

export type Listener<T extends Event> = (message: T) => void;

export const EventListenerProvider: FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const loginStatus = useSelector(selectPortalLoginStatus);

  if (loginStatus !== 'AUTHENTICATED') {
    return <>{children}</>;
  }

  return (
    <WebsocketProvider url={`${baseUrl}/portal/ws`}>
      {children}
    </WebsocketProvider>
  );
};

export type UseEventListener<T> = {
  state: State;
  send(message: T): void;
};

export function useEventListener<T extends Event>(
  type: T['type'],
  listener: Listener<T>,
  deps: DependencyList,
): UseEventListener<T> {
  const wrapper = useMemo<WebsocketListener>(
    () => (message) => {
      const data = JSON.parse(message.data);

      if (data?.type === type) {
        listener(data);
      }
    },
    [type, listener],
  );
  const websocket = useWebsocket(wrapper, [wrapper, ...deps]);

  return useMemo(
    () => ({
      state: websocket.state,
      send(message) {
        try {
          websocket.send(message);
        } catch (e) {
          console.warn('Failed to send message', e);
        }
      },
    }),
    [websocket.state, websocket.send],
  );
}
